import type { JSX } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Icon, type IconName } from '@/components/ui/icon';
import { useAppMode, isInitialSetupComplete } from '@/lib/appMode';
import { cn } from '@/lib/utils';

interface ModeOptionProps {
  title: string;
  description: string;
  icon: IconName;
  variant: 'demo' | 'real';
  onClick: () => void;
}

function ModeOption({ title, description, icon, variant, onClick }: ModeOptionProps): JSX.Element {
  const colors = {
    demo: {
      bg: 'bg-warning-100',
      border: 'border-warning-500',
      icon: 'text-warning-500',
      hover: 'hover:bg-warning-50',
    },
    real: {
      bg: 'bg-brand-blue-50',
      border: 'border-brand-blue-500',
      icon: 'text-brand-blue-500',
      hover: 'hover:bg-brand-blue-50/50',
    },
  };

  const c = colors[variant];

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-center gap-3 rounded-lg border-2 p-6 text-center transition-all',
        'focus:outline-none focus:ring-2 focus:ring-brand-blue-500 focus:ring-offset-2',
        c.border,
        c.hover
      )}
    >
      <div className={cn('rounded-full p-3', c.bg)}>
        <Icon name={icon} className={cn('icon-2xl', c.icon)} />
      </div>
      <div>
        <p className="font-semibold">{title}</p>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </div>
    </button>
  );
}

/**
 * Modal dialog shown on first app load to let users choose between
 * Demo mode (with sample data) or Real mode (empty production database).
 */
export function ModePicker(): JSX.Element | null {
  const { completeSetup } = useAppMode();

  // Check directly from localStorage to avoid hydration issues
  const isComplete = isInitialSetupComplete();

  // Don't show if setup is already complete
  if (isComplete) {
    return null;
  }

  return (
    <Dialog open={true}>
      <DialogContent
        className="sm:max-w-lg"
        // Prevent closing by clicking outside or pressing Escape
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="flex-col items-center">
          <DialogTitle className="text-center text-xl">Benvenuto in LM Sistema Tecnico</DialogTitle>
          <DialogDescription className="mt-2 text-center">Come vuoi iniziare?</DialogDescription>
        </DialogHeader>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <ModeOption
            title="Modalità Demo"
            description="Esplora l'app con dati di esempio. Perfetto per familiarizzare."
            icon="science"
            variant="demo"
            onClick={() => completeSetup('demo')}
          />
          <ModeOption
            title="Dati Reali"
            description="Inizia con un database vuoto per i tuoi dati di produzione."
            icon="database"
            variant="real"
            onClick={() => completeSetup('real')}
          />
        </div>

        <Alert variant="info" showIcon={false} className="mt-4 px-4">
          <AlertDescription className="text-center text-xs">
            Puoi cambiare modalità in qualsiasi momento dal menu in alto.
          </AlertDescription>
        </Alert>
      </DialogContent>
    </Dialog>
  );
}
